import type { OhlcValue, PlotRequest } from "@tento-data";
import { PlotCollection } from "@tento-data";

import type { CanvasRenderTarget } from "../canvas/canvas-types";
import type { CanvasSeriesRenderer } from "./renderer";
import { effectiveYExtent, toCanvasX, toCanvasY } from "./scale-utils";

export type CandlestickRendererStyle = Readonly<{
  risingFillStyle: string;
  fallingFillStyle: string;
  wickStrokeStyle: string;
  lineWidth: number;
  maxBodyWidth: number;
  slotPx: number;
}>;

/**
 * Canvas candlestick renderer for OHLC payloads.
 *
 * Candles are positioned **by date** (`toCanvasX`), so each candle sits under
 * its own time column and real overnight/weekend gaps show as gaps — the chart
 * lines up with the sheet's columns and with the date-mapped line/regression
 * renderers. When the raw series has more points than the canvas can show
 * legibly the points are **aggregated into wider OHLC bars** (open of the
 * first, max high, min low, close of the last in each bucket, positioned at the
 * bucket's midpoint date). Without aggregation a multi-day 15-minute series
 * collapses into a smear of sub-pixel ticks.
 */
export class CandlestickRenderer implements CanvasSeriesRenderer {
  constructor(private readonly style: CandlestickRendererStyle = {
    risingFillStyle: "#16a34a",
    fallingFillStyle: "#dc2626",
    wickStrokeStyle: "#111827",
    lineWidth: 1,
    maxBodyWidth: 12,
    slotPx: 9,
  }) {}

  supports(entry: PlotCollection.AnyEntry): boolean {
    return PlotCollection.isOhlcEntry(entry);
  }

  render(
    target: CanvasRenderTarget,
    request: PlotRequest,
    entry: PlotCollection.AnyEntry,
  ): void {
    if (!PlotCollection.isOhlcEntry(entry)) return;

    const raw = entry.series.range(
      request.viewRange.start,
      request.viewRange.end,
    ).points();
    const extent = effectiveYExtent(entry, request);

    if (raw.length === 0 || extent === null || extent === undefined) return;

    const { width, height } = target.size;
    const maxCandles = Math.max(1, Math.floor(width / this.style.slotPx));
    const candles = aggregateOhlc(raw, maxCandles);

    // Position by DATE so each candle sits under its own time column (real
    // overnight/weekend gaps show as gaps), matching the date-mapped
    // line/regression renderers.
    const centers = candles.map(([x]) =>
      toCanvasX(x, request.viewRange, width));
    // Date spacing is uneven (unlike the old ordinal layout), so clamp the body
    // to the TIGHTEST gap → bodies never overlap even when dates cluster.
    // Regular daily data keeps the standard slotPx-based width; only dense
    // clusters shrink. `maxCandles` still caps the count so bodies stay legible.
    let minGap = Number.POSITIVE_INFINITY;
    for (let i = 1; i < centers.length; i++) {
      const gap = centers[i] - centers[i - 1];
      if (gap < minGap) minGap = gap;
    }
    const bodyWidth = Math.max(
      1,
      Math.min(this.style.maxBodyWidth, this.style.slotPx * 0.7, minGap),
    );

    target.ctx.strokeStyle = this.style.wickStrokeStyle;
    target.ctx.lineWidth = this.style.lineWidth;

    for (let i = 0; i < candles.length; i++) {
      const [, value] = candles[i];
      const centerX = centers[i];
      const highY = toCanvasY(value.high, extent, height);
      const lowY = toCanvasY(value.low, extent, height);
      const openY = toCanvasY(value.open, extent, height);
      const closeY = toCanvasY(value.close, extent, height);

      target.ctx.beginPath();
      target.ctx.moveTo(centerX, highY);
      target.ctx.lineTo(centerX, lowY);
      target.ctx.stroke();

      const bodyTop = Math.min(openY, closeY);
      const bodyHeight = Math.abs(openY - closeY);

      target.ctx.fillStyle = value.close >= value.open
        ? this.style.risingFillStyle
        : this.style.fallingFillStyle;
      target.ctx.fillRect(
        centerX - bodyWidth / 2,
        bodyTop,
        bodyWidth,
        Math.max(bodyHeight, 1),
      );
    }
  }
}

/**
 * Collapse the raw OHLC points into at most `maxCandles` aggregated bars,
 * each spanning a contiguous run of points: open of the first, close of the
 * last, max high, min low, positioned at the bucket's midpoint date (`x`).
 * Returns the raw `[x, value]` points unchanged when already within budget.
 */
function aggregateOhlc(
  points: readonly (readonly [number, OhlcValue])[],
  maxCandles: number,
): readonly (readonly [number, OhlcValue])[] {
  const n = points.length;
  if (n <= maxCandles) return points;

  const perBucket = Math.ceil(n / maxCandles);
  const out: [number, OhlcValue][] = [];
  for (let i = 0; i < n; i += perBucket) {
    const end = Math.min(i + perBucket, n);
    const open = points[i][1].open;
    const close = points[end - 1][1].close;
    let high = Number.NEGATIVE_INFINITY;
    let low = Number.POSITIVE_INFINITY;
    for (let j = i; j < end; j++) {
      high = Math.max(high, points[j][1].high);
      low = Math.min(low, points[j][1].low);
    }
    // Midpoint date of the bucket's span, so the bar sits under the middle of
    // the dates it summarizes.
    const x = (points[i][0] + points[end - 1][0]) / 2;
    out.push([x, Object.freeze({ open, high, low, close })]);
  }
  return out;
}
